import { InputOptionBox, Button } from '../../../../core/components/Index';
import { Appl } from '../../../../core/Appl';
import ApplyViewModel from './ApplyViewModel';
import { useParams } from 'react-router-dom';
import useAsyncEffect from 'use-async-effect';
import { Steps } from './ApplyModels';
import Helper from '../../../../core/services/Helper';
import { observer } from 'mobx-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../../../../core/components/OptionSelectionAccordian';

const OptionSelection = observer(() => {
  const vm = Appl.Services.get<ApplyViewModel>('ApplyViewModel');
  let { memberId } = useParams();
  useAsyncEffect(async () => {
    await vm.loadStepAsync(Steps.OptionSelectionSelectYourOption, memberId!);
  }, []);

  return (
    <div className="space-y-6 max-w-4xl">
      <h1 className="text-32px tracking--1percent text-neutral-800 font-semibold mb-4">Option Election</h1>
      <div className="text-black text-15px tracking--1percent leading-26px space-y-4">
        <p>
          When you retire with SCERS, you have the flexibility to choose how your pension payments will be structured. These retirement options allow you to:
        </p>
        <ul className="list-disc pl-6 text-black text-15px tracking--1percent leading-26px">
          <li>Maximize your monthly benefit for yourself, or</li>
          <li>Provide for a loved one after your passing.</li>
        </ul>
        <p>
          Each option impacts the amount you receive during retirement and what, if anything, will be paid to a beneficiary after you are gone. While this might sound complex, rest assured—it's simpler than it seems, and we're here to guide you through the process to help you select the option that best suits you and your family.
        </p>
        <p>
          If you'd like to see how each option would affect your monthly benefit—or your survivor's benefit—you can use our{' '}
          Retirement Calculator to run an estimate. This tool will give you a clear idea of what to expect under different scenarios.
          {/* <a href={Appl.Setting.EnvironmentName === 'test' ? 'https://myscersqa.saccounty.gov/ToolsAndResources' : 'https://my.scers.gov/ToolsAndResources'} className="text-primary-500 underline" target="_blank">Retirement Calculator</a> to run an estimate. This tool will give you a clear idea of what to expect under different scenarios. */}
        </p>
        <p>
          While your selection is important, remember that you can change your option at any time up until your retirement date.
        </p>
      </div>
      <p className="text-15px font-bold text-black">Please select your option below.</p>
      <Accordion type="single" collapsible className="w-full">
        {vm.model.lovOptionSelections && vm.model.lovOptionSelections.map((option, index) => (
          <AccordionItem key={`beneficiaryPaymentOption${index}`} value={`item-${index}`}>
            <div className="flex items-center">
              <div className='flex-1 [&_label]:py-2 [&_label]:pt-2.5'>
                <InputOptionBox
                  inputId={`beneficiaryPaymentOptionId-${option.id}`}
                  label={Helper.formattedHtml(option.name!)}
                  value={option.id}
                  checked={vm.model.optionSelection.beneficiaryPaymentOptionId?.toString() === option.id?.toString()}
                  onChange={(e) => {
                    if (vm.model.optionSelection.beneficiaryPaymentOptionId?.toString() === option.id?.toString()) {
                      vm.onInputChange({ target: { value: null } }, 'beneficiaryPaymentOptionId', vm.model.optionSelection);
                    } else {
                      vm.onInputChange(e, 'beneficiaryPaymentOptionId', vm.model.optionSelection);
                    }
                  }}
                  className="flex items-center"
                /></div>
              <AccordionTrigger className="flex-none px-4  ">
                <span className="sr-only">Toggle details</span>
              </AccordionTrigger>
            </div>
            <AccordionContent>
              <div className="text-base">
                {Helper.formattedHtml(option.description)}
              </div>
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
      <div className="flex mt-6 flex-wrap gap-2 sm:flex-nowrap">
        <Button
          label="Save & Continue"
          rounded
          onClick={async () => await vm.onOptionSelectionSelectYourOptionStepAsync()}
          disabled={vm.model.optionSelection.beneficiaryPaymentOptionId! < 1}
        />
        {vm.navigatedFromReviewScreen && <Button title="Cancel & Return to Review" severity='secondary' rounded
          onClick={async () => await vm.navigateToReviewStepAsync()}
        />}
      </div>
    </div>
  );
});

export default OptionSelection;
